import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAlerts, type Severity } from '@/lib/alerts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { SeverityBadge } from '@/components/SeverityBadge'
import { relative } from '@/lib/format'

export function AlertsPage() {
  const [openOnly, setOpenOnly] = useState(true)
  const [severity, setSeverity] = useState<Severity | null>(null)
  const { data, isLoading } = useAlerts(openOnly)

  const filtered = (data || []).filter((a) =>
    severity ? a.severity === severity : true,
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Alerts</h1>
          <p className="text-muted-foreground">
            Live + recent alerts. Silence a fingerprint to stop notifications.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={openOnly ? 'default' : 'ghost'}
            onClick={() => setOpenOnly(!openOnly)}
          >
            {openOnly ? 'Open only' : 'All'}
          </Button>
          {(['critical', 'error', 'warning', 'info'] as Severity[]).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={severity === s ? 'default' : 'ghost'}
              onClick={() => setSeverity(severity === s ? null : s)}
            >
              {s}
            </Button>
          ))}
        </div>
      </div>

      {isLoading && <Skeleton className="h-40" />}

      {!isLoading && filtered.length === 0 && (
        <Card>
          <CardContent className="pt-6 text-muted-foreground">
            No alerts right now. 🎉
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {filtered.map((a) => (
          <Link key={a.id} to={`/alerts/${a.id}`} className="block">
            <Card className="hover:border-primary/50 transition cursor-pointer">
              <CardHeader className="flex-row items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <SeverityBadge severity={a.severity} />
                  <CardTitle className="text-base">{a.title}</CardTitle>
                </div>
                <div className="text-xs text-muted-foreground">
                  {a.closed_at ? `closed ${relative(a.closed_at)}` : `opened ${relative(a.opened_at)}`}
                </div>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground flex gap-4 pb-4">
                <span>Rule: {a.rule_name}</span>
                {a.entity_name && <span>Entity: {a.entity_name}</span>}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  )
}
